// PaymentSuccessPage.jsx
import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Navbar from "./Navbar";
import Footer from "./Footer";

const PaymentSuccessPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [paymentData, setPaymentData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const getImageUrl = (item) => {
    if (item.item_type === 'combo' && item.combo_image) {
      const imageUrl = item.combo_image;
      return imageUrl.startsWith('http') ? imageUrl : `https://evercorenutrition.com${imageUrl}`;
    }
    
    const imageUrl = 
      item.product_data?.main_image ||
      item.product_data?.variant?.image ||
      item.variant_image || 
      item.product_image ||
      item.image;
    
    if (!imageUrl) return '/assets/images/Yeast_Protein.png';
    return imageUrl.startsWith('http') ? imageUrl : `https://evercorenutrition.com${imageUrl}`;
  };

  const getProductName = (item) => {
    if (item.item_type === 'combo') {
      return item.combo_name || item.name || 'Combo Product';
    }
    
    if (item.product_data?.name) {
      return item.product_data.name;
    }
    
    if (item.product_name) {
      const cleanName = item.product_name.split(' (From Combo:')[0];
      return cleanName;
    }
    
    return 'Unknown Product';
  };

  const getFlavour = (item) => {
    if (item.item_type === 'combo') return null;
    
    if (item.product_data?.variant?.flavour) {
      const flavour = item.product_data.variant.flavour;
      if (flavour !== 'Default' && flavour !== 'default') return flavour;
    }
    
    if (item.product_data?.flavour) {
      const flavour = item.product_data.flavour;
      if (flavour !== 'Default' && flavour !== 'default') return flavour;
    }
    
    if (item.variant_details?.flavour) {
      const flavour = item.variant_details.flavour;
      if (flavour !== 'Default' && flavour !== 'default') return flavour;
    }
    
    return null;
  };

  const getWeight = (item) => {
    if (item.item_type === 'combo') return null;
    
    if (item.product_data?.variant) {
      const variant = item.product_data.variant;
      if (variant.weight_value && variant.weight_unit) {
        return `${variant.weight_value}${variant.weight_unit}`;
      }
      if (variant.weight_display) return variant.weight_display;
    }
    
    if (item.variant_details?.weight) return item.variant_details.weight;
    return null;
  };

  const getVariantSku = (item) => {
    if (item.item_type === 'combo') return 'COMBO';
    
    if (item.product_data?.variant?.sku) return item.product_data.variant.sku;
    if (item.variant_details?.sku) return item.variant_details.sku;
    if (item.product_data?.sku) return item.product_data.sku;
    return 'N/A';
  };

  const getMrp = (item) => {
    if (item.item_type === 'combo') return null;
    
    if (item.product_data?.variant?.mrp) return item.product_data.variant.mrp;
    if (item.variant_details?.mrp) return item.variant_details.mrp;
    return null;
  };

  const formatCurrency = (value) => {
    if (value === undefined || value === null) return '₹0.00';
    const num = parseFloat(value);
    if (isNaN(num)) return '₹0.00';
    return new Intl.NumberFormat('en-IN', { 
      style: 'currency', 
      currency: 'INR', 
      minimumFractionDigits: 2 
    }).format(num);
  };

  useEffect(() => {
    const fetchPaymentData = async () => {
      const transactionId = searchParams.get('transaction_id');
      
      if (!transactionId) {
        setError('No transaction ID found');
        setLoading(false);
        return;
      }

      try {
        const token = localStorage.getItem('authToken');
        const response = await fetch(
          `https://evercorenutrition.com/ev_admin/api/payment/success/?transaction_id=${transactionId}`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              ...(token && { Authorization: `Bearer ${token}` })
            }
          }
        );
        
        if (response.ok) {
          const data = await response.json();
          if (data.success && data.payment_status === 'completed' && data.is_successful) {
            setPaymentData(data);
          } else {
            setError('Payment Pending .... Please wait while we confirm your payment.');
          }
        } else {
          setError('Failed to fetch payment details');
        }
      } catch (err) {
        console.error('Error fetching payment:', err);
        setError('Network error. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchPaymentData();
  }, [searchParams]);

  const handleContinueShopping = () => {
    navigate('/');
  };

  const handleViewOrders = () => {
    navigate('/my-orders');
  };

  const handlePrint = () => {
    window.print();
  };

  if (loading) {
    return (
      <>
        <Navbar />
        <section className="cart-section section-b-space">
          <div className="container-fluid-lg" style={{ textAlign: 'center', padding: '60px' }}>
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p style={{ marginTop: '10px', color: '#666' }}>Loading payment details...</p>
          </div>
        </section>
        <Footer />
      </>
    );
  }

  if (error || !paymentData) {
    return (
      <>
        <Navbar />
        <section className="cart-section section-b-space">
          <div className="container-fluid-lg" style={{ textAlign: 'center', padding: '40px' }}>
            <div style={{ fontSize: '48px', marginBottom: '20px' }}>⚠️</div>
            {/* <h4 style={{ color: '#dc3545', marginBottom: '10px' }}>Payment Error</h4> */}
            <h4 style={{ color: '#666' }}>{error || 'Payment information not found'}</h4>
            <button onClick={handleContinueShopping} className="btn btn-dark mt-3">
              Continue Shopping
            </button>
          </div>
        </section>
        <Footer />
      </>
    );
  }

  const { transaction_id, order_id, formatted_amount, payment_date, order_details, transaction_details } = paymentData;
  const orderItems = order_details?.items || [];
  const subtotal = transaction_details?.subtotal ?? order_details?.subtotal ?? 0;
  const discount = transaction_details?.discount_amount ?? order_details?.discount ?? 0;
  const shipping = transaction_details?.shipping_charge ?? order_details?.shipping ?? 0;
  const totalAmount = transaction_details?.total_amount ?? order_details?.total ?? paymentData.amount ?? 0;
  const coupon = transaction_details?.coupon_code ?? order_details?.coupon_code ?? null;
  const pickupAddress = order_details?.pickup_address || transaction_details?.pickup_address || "No address provided";

  return (
    <>
      <Navbar />

      {/* Success Banner - Similar to Placeorder */}
      <section className="breadcrumb-section pt-0">
        <div className="container-fluid-lg">
          <div className="row">
            <div className="col-12">
              <div className="breadcrumb-contain breadcrumb-order" style={{ display: "flex", justifyContent: "center" }}>
                <div className="order-box">
                  <div className="order-image">
                    <div className="checkmark">
                      {[...Array(6)].map((_, i) => (
                        <svg key={i} className="star" height="19" viewBox="0 0 19 19" width="19" xmlns="http://www.w3.org/2000/svg">
                          <path d="M8.296.747c.532-.972 1.393-.973 1.925 0l2.665 4.872 4.876 2.66c.974.532.975 1.393 0 1.926l-4.875 2.666-2.664 4.876c-.53.972-1.39.973-1.924 0l-2.664-4.876L.76 10.206c-.972-.532-.973-1.393 0-1.925l4.872-2.66L8.296.746z"></path>
                        </svg>
                      ))}
                      <svg className="checkmark__check" height="36" viewBox="0 0 48 36" width="48" xmlns="http://www.w3.org/2000/svg">
                        <path d="M47.248 3.9L43.906.667a2.428 2.428 0 0 0-3.344 0l-23.63 23.09-9.554-9.338a2.432 2.432 0 0 0-3.345 0L.692 17.654a2.236 2.236 0 0 0 .002 3.233l14.567 14.175c.926.894 2.42.894 3.342.01L47.248 7.128c.922-.89.922-2.34 0-3.23"></path>
                      </svg>
                      <svg className="checkmark__background" height="115" viewBox="0 0 120 115" width="120" xmlns="http://www.w3.org/2000/svg">
                        <path d="M107.332 72.938c-1.798 5.557 4.564 15.334 1.21 19.96-3.387 4.674-14.646 1.605-19.298 5.003-4.61 3.368-5.163 15.074-10.695 16.878-5.344 1.743-12.628-7.35-18.545-7.35-5.922 0-13.206 9.088-18.543 7.345-5.538-1.804-6.09-13.515-10.696-16.877-4.657-3.398-15.91-.334-19.297-5.002-3.356-4.627 3.006-14.404 1.208-19.962C10.93 67.576 0 63.442 0 57.5c0-5.943 10.93-10.076 12.668-15.438 1.798-5.557-4.564-15.334-1.21-19.96 3.387-4.674 14.646-1.605 19.298-5.003C35.366 13.73 35.92 2.025 41.45.22c5.344-1.743 12.628 7.35 18.545 7.35 5.922 0 13.206-9.088 18.543-7.345 5.538 1.804 6.09 13.515 10.696 16.877 4.657 3.398 15.91.334 19.297 5.002 3.356 4.627-3.006 14.404-1.208 19.962C109.07 47.424 120 51.562 120 57.5c0 5.943-10.93 10.076-12.668 15.438z"></path>
                      </svg>
                    </div>
                  </div>

                  <div className="order-contain">
                    <h3 className="theme-color" style={{ color: '#28a745' }}>Payment Successful!</h3>
                    <h6>Transaction ID: <strong>{transaction_id}</strong></h6>
                    <h6>Order ID: <strong>{order_id}</strong></h6>
                    <h5 className="mt-2" style={{ color: '#0d6efd' }}>Amount Paid: {formatted_amount || formatCurrency(paymentData.amount)}</h5>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="cart-section section-b-space">
        <div className="container-fluid-lg">
          <div className="row g-sm-4 g-3">

            {/* Left Column - Order Items */}
            <div className="col-xxl-9 col-lg-8">
              <div className="cart-table order-table order-table-2">
                
                {/* Desktop View */}
                <div className="d-none d-md-block">
                  <div className="table-responsive">
                    <table className="table mb-0" style={{ minWidth: '100%' }}>
                      <thead>
                        <tr>
                          <th style={{ width: '45%' }}>Product</th>
                          <th style={{ width: '15%' }}>Unit Price</th>
                          <th style={{ width: '15%' }}>Quantity</th>
                          <th style={{ width: '25%' }}>Total Price</th>
                        </tr>
                      </thead>
                      <tbody>
                        {orderItems.map((item, index) => {
                          const itemTotal = (parseFloat(item.price) * item.quantity).toFixed(2);
                          const productName = getProductName(item);
                          const flavour = getFlavour(item);
                          const weight = getWeight(item);
                          const variantSku = getVariantSku(item);
                          const mrp = getMrp(item);
                          const isComboItem = item.item_type === 'combo';
                          const comboName = item.combo_name;
                          const imageUrl = getImageUrl(item);
                          
                          return (
                            <tr key={item.id || index}>
                              <td className="product-detail">
                                <div className="product border-0 d-flex align-items-start gap-3">
                                  <div className="product-image" style={{ width: '70px', flexShrink: 0 }}>
                                    <img
                                      src={imageUrl}
                                      className="img-fluid blur-up lazyload"
                                      alt={productName}
                                      style={{ width: '100%', objectFit: 'contain' }}
                                      onError={(e) => {
                                        e.target.onerror = null;
                                        e.target.src = '/assets/images/Yeast_Protein.png';
                                      }}
                                    />
                                  </div>
                                  <div className="product-detail flex-grow-1">
                                    <ul style={{ paddingLeft: 0, marginBottom: 0, listStyle: 'none' }}>
                                      <li className="name mb-1">
                                        <span className="fw-semibold text-dark">{productName}</span>
                                      </li>
                                      
                                      {isComboItem && comboName && (
                                        <li className="text-content mb-1">
                                          <span style={{ 
                                            background: '#e8f0fe', 
                                            padding: '2px 8px', 
                                            borderRadius: '12px',
                                            fontSize: '11px',
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: '4px',
                                            color: '#0d6efd'
                                          }}>
                                            <i className="fa-solid fa-layer-group" style={{ fontSize: '10px' }}></i>
                                            {comboName.length > 50 ? comboName.substring(0, 50) + '...' : comboName}
                                          </span>
                                        </li>
                                      )}
                                      
                                      {flavour && (
                                        <li className="text-content mb-1">
                                          <span style={{ 
                                            background: '#f3f4f6', 
                                            padding: '2px 8px', 
                                            borderRadius: '4px',
                                            fontSize: '11px'
                                          }}>
                                            <i className="fa-solid fa-flask me-1"></i> {flavour}
                                          </span>
                                        </li>
                                      )}
                                      
                                      {weight && (
                                        <li className="text-content mb-1">
                                          <span style={{ 
                                            background: '#f3f4f6', 
                                            padding: '2px 8px', 
                                            borderRadius: '4px',
                                            fontSize: '11px'
                                          }}>
                                            <i className="fa-solid fa-weight-scale me-1"></i> {weight}
                                          </span>
                                        </li>
                                      )}
                                    </ul>
                                  </div>
                                </div>
                              </td>

                              <td className="price align-middle">
                                <h6 className="mb-0 theme-color">₹{parseFloat(item.price).toFixed(2)}</h6>
                                {mrp && mrp > parseFloat(item.price) && (
                                  <small style={{ textDecoration: 'line-through', color: '#999', fontSize: '10px' }}>
                                    MRP: ₹{mrp.toFixed(2)}
                                  </small>
                                )}
                              </td>

                              <td className="quantity align-middle">
                                <h5 className="mb-0">{String(item.quantity).padStart(2, '0')}</h5>
                              </td>

                              <td className="subtotal align-middle">
                                <h5 className="mb-0 fw-bold">₹{itemTotal}</h5>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Mobile View */}
                <div className="d-md-none">
                  {orderItems.map((item, index) => {
                    const itemTotal = (parseFloat(item.price) * item.quantity).toFixed(2);
                    const productName = getProductName(item);
                    const flavour = getFlavour(item);
                    const weight = getWeight(item);
                    const isComboItem = item.item_type === 'combo';
                    const comboName = item.combo_name;
                    const imageUrl = getImageUrl(item);
                    
                    return (
                      <div key={item.id || index} className="order-card-mobile mb-3" style={{ 
                        background: '#fff',
                        borderRadius: '12px',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                        overflow: 'hidden'
                      }}>
                        <div className="p-3">
                          <div className="d-flex gap-3" style={{ flexWrap: 'wrap' }}>
                            <div style={{ width: '80px', flexShrink: 0 }}>
                              <img
                                src={imageUrl}
                                className="img-fluid rounded"
                                alt={productName}
                                style={{ width: '100%', objectFit: 'contain', maxHeight: '80px' }}
                                onError={(e) => {
                                  e.target.onerror = null;
                                  e.target.src = '/assets/images/Yeast_Protein.png';
                                }}
                              />
                            </div>
                            
                            <div style={{ flex: '1', minWidth: '180px' }}>
                              <h6 className="mb-1 fw-bold" style={{ fontSize: '14px' }}>{productName}</h6>
                              
                              {isComboItem && comboName && (
                                <div className="mb-2">
                                  <span style={{ 
                                    background: '#e8f0fe', 
                                    padding: '2px 8px', 
                                    borderRadius: '12px',
                                    fontSize: '10px',
                                    color: '#0d6efd'
                                  }}>
                                    <i className="fa-solid fa-layer-group me-1"></i>
                                    {comboName.length > 35 ? comboName.substring(0, 35) + '...' : comboName}
                                  </span>
                                </div>
                              )}
                              
                              <div className="d-flex flex-wrap gap-2 mb-2">
                                {flavour && (
                                  <span className="badge bg-light text-dark" style={{ fontSize: '11px' }}>
                                    <i className="fa-solid fa-flask me-1"></i> {flavour}
                                  </span>
                                )}
                                {weight && (
                                  <span className="badge bg-light text-dark" style={{ fontSize: '11px' }}>
                                    <i className="fa-solid fa-weight-scale me-1"></i> {weight}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                          
                          <div className="mt-3 pt-2 border-top d-flex justify-content-between align-items-center">
                            <div>
                              <span className="text-muted" style={{ fontSize: '12px' }}>Price:</span>
                              <span className="fw-bold ms-1">₹{parseFloat(item.price).toFixed(2)}</span>
                            </div>
                            <div>
                              <span className="text-muted" style={{ fontSize: '12px' }}>Qty:</span>
                              <span className="fw-bold ms-1">{item.quantity}</span>
                            </div>
                            <div className="text-end">
                              <span className="text-muted d-block" style={{ fontSize: '11px' }}>Total</span>
                              <span className="fw-bold text-primary">₹{itemTotal}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Right Column - Summary */}
            <div className="col-xxl-3 col-lg-4">
              <div className="row g-4">

                <div className="col-lg-12 col-sm-6">
                  <div className="summery-box">
                    <div className="summery-header">
                      <h3>Price Details</h3>
                      <h5 className="ms-auto theme-color">({orderItems.length} Items)</h5>
                    </div>
                    <ul className="summery-contain">
                      <li>
                        <h4>Subtotal</h4>
                        <h4 className="price">{formatCurrency(subtotal)}</h4>
                      </li>
                      {discount > 0 && (
                        <li>
                          <h4>Discount {coupon ? `(${coupon})` : ''}</h4>
                          <h4 className="price theme-color">- {formatCurrency(discount)}</h4>
                        </li>
                      )}
                      <li>
                        <h4>Shipping</h4>
                        <h4 className="price">{shipping > 0 ? formatCurrency(shipping) : 'Free'}</h4>
                      </li>
                    </ul>
                    <ul className="summery-total">
                      <li className="list-total">
                        <h4>Total Paid</h4>
                        <h4 className="price">{formatCurrency(totalAmount)}</h4>
                      </li>
                    </ul>
                  </div>
                </div>

                <div className="col-lg-12 col-sm-6">
                  <div className="summery-box">
                    <div className="summery-header d-block">
                      <h3>Pickup Address</h3>
                    </div>
                    <ul className="summery-contain pb-0 border-bottom-0">
                      <li className="d-block">
                        <h4>{pickupAddress}</h4>
                      </li>
                      <li className="pb-0">
                        <h4>Payment Method:</h4>
                        <h4 className="price">PhonePe (Prepaid)</h4>
                      </li>
                      <li className="pb-0">
                        <h4>Payment Date:</h4>
                        <h4 className="price">{new Date(payment_date).toLocaleDateString('en-IN')}</h4>
                      </li>
                    </ul>
                  </div>
                </div>

                <div className="col-12">
                  <div className="summery-box">
                    <div className="summery-header d-block">
                      <h3>What would you like to do next?</h3>
                    </div>
                    <ul className="summery-contain pb-0 border-bottom-0">
                      <li className="d-block pt-0">
                        <div className="d-flex flex-column gap-3">
                          <button 
                            onClick={handleContinueShopping}
                            className="btn btn-primary w-100"
                            style={{ padding: '12px', fontWeight: '600', borderRadius: '8px' }}
                          >
                            <i className="fa-solid fa-store me-2"></i>
                            Continue Shopping
                          </button>
                         
                        </div>
                      </li>
                    </ul>
                  </div>
                </div>

              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />

      <style>{`
        @media print {
          .breadcrumb-section, .cart-section .btn, Navbar, Footer, .col-xxl-3 .btn {
            display: none !important;
          }
          .col-xxl-9, .col-lg-8 {
            width: 100% !important;
          }
        }
      `}</style>
    </>
  );
};

export default PaymentSuccessPage;